import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Stack, Label, TextField, PrimaryButton, DefaultButton, MessageBar, MessageBarType, Spinner } from '@fluentui/react';
import { getUserPromptExampleData, updateUserPromptExample } from '../../../api/admin/api_admin';
import type { UserPromptExampleData } from '../../../api/admin/models_admin';
import { AdminLabel, ErrorDialog } from "../components";

export const UserPromptExample: React.FC = () => {
    const { seq } = useParams<{ seq: string }>();
    const navigate = useNavigate();
    const [data, setData] = useState<UserPromptExampleData | null>(null);
    const [prompts, setPrompts] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!seq) {
                setError('データの取得に失敗しました');
                return;
            }
            try {
                setLoading(true);
                const result = await getUserPromptExampleData(seq);
                if ("error" in result) {
                    setError(result.error || 'データの取得に失敗しました');
                } else {
                    setData(result);
                    setPrompts(result.user_prompt_examples);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [seq, navigate]);

    if (loading) {
        return (
            <Stack tokens={{ padding: 20 }}>
                <Spinner label="読み込み中..." />
            </Stack>
        );
    }

    if (!data) {
        return (
            <Stack tokens={{ padding: 20 }}>
                <MessageBar messageBarType={MessageBarType.error}>
                    データが見つかりません
                </MessageBar>
            </Stack>
        );
    }

    return (
        <Stack tokens={{ padding: 20, childrenGap: 20 }}>
            <AdminLabel text='サンプルプロンプト' />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <Stack tokens={{ childrenGap: 30 }}>
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <Label styles={{ root: { width: 150, fontWeight: 600 } }}>カテゴリ名</Label>
                    <Label>{data.container_name}</Label>
                </Stack>

                {Object.entries(prompts).sort(([a], [b]) => parseInt(a) - parseInt(b)).map(([key, value]) => (
                    <Stack key={key} horizontal tokens={{ childrenGap: 10 }}>
                        <Label styles={{ root: { width: 150, fontWeight: 600 } }}>
                            サンプルプロンプト{key}
                        </Label>
                        <Stack styles={{ root: { flex: 1 } }} tokens={{ childrenGap: 10 }}>
                            <TextField
                                multiline
                                rows={15}
                                value={value}
                                readOnly
                                styles={{ root: { width: '100%' }, field: { fontSize: 'large' } }}
                            />
                            <DefaultButton
                                onClick={() => {
                                    window.opener.postMessage(value, '*');
                                    window.close();
                                }}
                                styles={{ root: { width: 'fit-content' } }}
                            >テキストボックスへ入力</DefaultButton>
                        </Stack>
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
};